import { useEffect, useState } from "react";
import { AlertTriangle, Search, UserMinus, UserPlus, Users } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { initialsOf } from "@/lib/tickets";
import { ErrorNote, type AdminTeam } from "./shared";
import { ConfirmPanel } from "@/components/Confirm";

/**
 * Who is on one support team, and in what role.
 *
 * ⚠️ This is a PHI access surface, not an org chart. Sensitivity clearance comes from team
 * membership: both enforcement layers require a request's classification to be in the
 * reader's cleared set before any scope — including Global — is considered. So adding
 * somebody to EMR is what lets them read clinical requests, and removing them is what stops
 * it.
 *
 * ⚠️ A role can also carry clearance of its own (D-28), which is why the role pickers mark
 * those with a ⚠ — putting somebody on a Standard-only team in a role that clears Clinical
 * grants Clinical, and this team's own clearance line would not say so.
 *
 * Rendered inside a team's card on the Support Teams tab, so the team's name, description
 * and clearance are already on screen above it and are deliberately not repeated here.
 *
 * Nothing here is an authorization decision. The endpoints re-check the actor's own
 * permissions and clearance, refuse a grant wider than the actor holds, and audit every
 * write at high severity.
 */

interface TeamMember {
  membershipId: string;
  userId: string;
  displayName: string;
  email: string | null;
  roleId: string;
  roleName: string;
  addedAtUtc: string;
}

interface DirectoryUser {
  id: string;
  displayName: string;
  email: string | null;
}

interface RoleSummary {
  id: string;
  name: string;
  isSystem: boolean;
  clearedLevels: string[];
}

export function TeamMembers({
  team,
  onChanged,
}: {
  team: AdminTeam;

  /**
   * Called after a membership is added, removed or re-roled.
   *
   * ⚠️ Not cosmetic. The parent's member count decides whether Delete will be refused and
   * whether the card warns that nobody can work the queue — a count that lagged behind the
   * list right beside it would be the screen contradicting itself about access.
   */
  onChanged?: () => Promise<void>;
}) {
  const [members, setMembers] = useState<TeamMember[] | null>(null);
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  /** Which membership is being removed, while its question is on screen. */
  const [confirming, setConfirming] = useState<TeamMember | null>(null);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      setMembers(await api<TeamMember[]>(`/api/teams/${team.id}/members`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load members.");
      setMembers([]);
    }
  };

  useEffect(() => {
    void load();
    void api<RoleSummary[]>("/api/roles")
      .then(setRoles)
      .catch(() => setRoles([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team.id]);

  async function remove(member: TeamMember) {
    setBusy(member.membershipId);
    setError(null);

    try {
      await api(`/api/teams/${team.id}/members/${member.membershipId}`, { method: "DELETE" });
      await load();
      await onChanged?.();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That member could not be removed.");
    } finally {
      setBusy(null);
    }
  }

  /**
   * ⚠️ One request, not remove-then-add.
   *
   * Those two calls can interleave with anything else, and the window between them is a
   * person with no clearance in the middle of a shift. It would also re-stamp when they
   * joined the team, which is the fact an investigation asks for. The server re-runs the
   * same guard it applies to adding somebody, so this cannot hand out a role the actor
   * could not have added them with.
   */
  async function changeRole(member: TeamMember, roleId: string) {
    setBusy(member.membershipId);
    setError(null);

    try {
      await api(`/api/teams/${team.id}/members/${member.membershipId}`, {
        method: "PUT",
        body: { roleId },
      });
      await load();
      await onChanged?.();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That role could not be changed.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Members
        </h3>

        <button
          type="button"
          onClick={() => setAdding((a) => !a)}
          aria-expanded={adding}
          className="inline-flex h-9 items-center gap-2 rounded-lg border border-input px-3 text-sm transition hover:border-ring"
        >
          <UserPlus className="h-4 w-4" aria-hidden />
          Add member
        </button>
      </div>

      {error && (
        <div className="mt-2">
          <ErrorNote message={error} />
        </div>
      )}

      {adding && (
        <AddMemberForm
          teamId={team.id}
          roles={roles}
          existing={members ?? []}
          onAdded={async () => {
            setAdding(false);
            await load();
            await onChanged?.();
          }}
          onError={setError}
        />
      )}

      {members === null ? (
        <div className="mt-3 space-y-2" aria-busy="true">
          {[0, 1].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      ) : members.length === 0 ? (
        <div className="mt-3 rounded-xl border border-dashed border-border px-4 py-8 text-center">
          <Users className="mx-auto h-7 w-7 text-muted-foreground" aria-hidden />
          <p className="mt-2 text-sm font-medium">Nobody is on this team</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
            Its queue is unreachable until somebody is. Requests in these categories stay visible
            only to the people who raised them.
          </p>
        </div>
      ) : (
        <ul className="mt-2 divide-y divide-border">
          {members.map((m) => (
            <li key={m.membershipId} className="flex items-center gap-3 py-2">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent text-xs font-semibold">
                {initialsOf(m.displayName)}
              </span>

              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{m.displayName}</p>
                {/* Email, so two people sharing a display name are distinguishable —
                    getting this wrong hands the wrong person clinical access. */}
                <p className="truncate text-xs text-muted-foreground">{m.email ?? "No email"}</p>
              </div>

              <select
                value={m.roleId}
                disabled={busy === m.membershipId || roles.length === 0}
                onChange={(e) => void changeRole(m, e.target.value)}
                aria-label={`Role for ${m.displayName} on ${team.name}`}
                className="h-9 w-32 shrink-0 rounded-lg border border-input bg-background px-2 text-base sm:w-40 md:text-sm"
              >
                {/* The current role is included even if it is somehow missing from the
                    list, so the control never silently shows somebody the wrong one. */}
                {!roles.some((r) => r.id === m.roleId) && (
                  <option value={m.roleId}>{m.roleName}</option>
                )}
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                    {r.clearedLevels.length > 0 ? " ⚠" : ""}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={() => setConfirming(m)}
                disabled={busy === m.membershipId}
                aria-label={`Remove ${m.displayName} from ${team.name}`}
                className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-input px-2.5 text-sm text-muted-foreground transition hover:border-destructive/50 hover:text-destructive disabled:opacity-50"
              >
                <UserMinus className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">Remove</span>
              </button>

              {/* ⚠️ This one is an ACCESS change, not a tidy-up: membership of a team IS
                  clearance to read its classifications, so removing somebody takes away
                  requests they can currently open. Worth a question even though it is
                  reversible — re-adding them is easy, but nobody re-adds access they did not
                  notice removing. */}
              {confirming?.membershipId === m.membershipId && (
                <div className="w-full">
                  <ConfirmPanel
                    question={`Remove ${m.displayName} from ${team.name}?`}
                    detail="They lose the clearance this team gives them and any request they could only read through it. Their own requests and every other team they are on are untouched."
                    confirmLabel="Remove from team"
                    onConfirm={async () => {
                      await remove(m);
                      setConfirming(null);
                    }}
                    onCancel={() => setConfirming(null)}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AddMemberForm({
  teamId,
  roles,
  existing,
  onAdded,
  onError,
}: {
  teamId: string;
  roles: RoleSummary[];
  existing: TeamMember[];
  onAdded: () => Promise<void>;
  onError: (message: string | null) => void;
}) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<DirectoryUser[]>([]);
  const [picked, setPicked] = useState<DirectoryUser | null>(null);
  const [roleId, setRoleId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (picked) return;

    // Debounced. This searches a staff directory, and a request per keystroke is both
    // wasteful and noise in the audit trail of who looked up whom.
    const handle = setTimeout(() => {
      const qs = search ? `?search=${encodeURIComponent(search)}` : "";
      void api<DirectoryUser[]>(`/api/users${qs}`)
        .then((u) => setResults(u.filter((x) => !existing.some((m) => m.userId === x.id))))
        .catch(() => setResults([]));
    }, 250);

    return () => clearTimeout(handle);
  }, [search, picked, existing]);

  async function submit() {
    if (!picked || !roleId) return;

    setSaving(true);
    onError(null);

    try {
      await api(`/api/teams/${teamId}/members`, {
        method: "POST",
        body: { userId: picked.id, roleId },
      });

      setPicked(null);
      setSearch("");
      setRoleId("");
      await onAdded();
    } catch (e) {
      // Surfaced verbatim. The server refuses a grant wider than the actor holds and
      // names the permissions or levels involved, which is more use than "forbidden".
      onError(e instanceof ApiError ? e.message : "That member could not be added.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-3 rounded-xl border border-border bg-muted/30 p-3">
      <div className="grid gap-3 sm:grid-cols-[1fr_12rem_auto] sm:items-start">
        <div>
          <label htmlFor="member-search" className="mb-1 block text-xs font-medium">
            Person
          </label>

          {picked ? (
            <div className="flex h-10 items-center justify-between gap-2 rounded-lg border border-input bg-background px-3">
              <span className="min-w-0 truncate text-sm">
                {picked.displayName}
                <span className="text-muted-foreground"> · {picked.email ?? "No email"}</span>
              </span>
              <button
                type="button"
                onClick={() => setPicked(null)}
                className="shrink-0 text-xs text-muted-foreground underline"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <input
                id="member-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email"
                autoComplete="off"
                className="h-10 w-full rounded-lg border border-input bg-background pl-8 pr-3 text-base focus:border-ring focus:outline-none md:text-sm"
              />
            </div>
          )}
        </div>

        <div>
          <label htmlFor="member-role" className="mb-1 block text-xs font-medium">
            Role on this team
          </label>
          <select
            id="member-role"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            className="h-10 w-full rounded-lg border border-input bg-background px-2 text-base md:text-sm"
          >
            <option value="">Choose a role…</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {/* Marked because such a role widens clearance beyond what this team
                    clears, and the team's own clearance line would not say so. */}
                {r.clearedLevels.length > 0 ? " ⚠" : ""}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={() => void submit()}
          disabled={!picked || !roleId || saving}
          className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50 sm:mt-[1.375rem]"
        >
          {saving ? "Adding…" : "Add"}
        </button>
      </div>

      {!picked && search && (
        <ul className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-border bg-background">
          {results.length === 0 ? (
            <li className="px-3 py-2 text-sm text-muted-foreground">
              Nobody matches. People appear here after they sign in for the first time.
            </li>
          ) : (
            results.slice(0, 25).map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  onClick={() => setPicked(u)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                >
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-accent text-[10px] font-semibold">
                    {initialsOf(u.displayName)}
                  </span>
                  <span className="min-w-0 truncate">
                    {u.displayName}
                    <span className="text-muted-foreground"> · {u.email ?? "No email"}</span>
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}

      <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        This grants access to patient- and personnel-linked requests, and is recorded against your
        name in the activity log.
      </p>
    </div>
  );
}
